import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';

import { usePersistedSetting } from '@@common';
import { CopyToClipboardButton } from '@@components';

import DialogHeaderActions from './DialogHeaderActions';

export interface SaveValidationIssue {
	code: string;
	path: string;
	message: string;
}

export interface SaveError {
	title: string;
	detail: string;
	traceId?: string;
	validationIssues: SaveValidationIssue[];
}

interface SaveErrorDialogProps {
	error?: SaveError;
	onClose: () => void;
	settingsKey?: string;
}

const DIALOG_MAXIMIZED_KEY = 'saveErrorDialog.maximized';

export default function SaveErrorDialog({ error, onClose, settingsKey = DIALOG_MAXIMIZED_KEY }: SaveErrorDialogProps) {
	const [isMaximized, setIsMaximized] = usePersistedSetting(settingsKey, false);

	return (
		<Dialog
			open={!!error}
			onClose={onClose}
			fullScreen={isMaximized}
			fullWidth
			maxWidth={false}
			PaperProps={{
				sx: isMaximized
					? { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', overflow: 'hidden' }
					: { width: 'min(720px, 95vw)', maxHeight: '90vh', overflow: 'hidden' },
			}}
		>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<ErrorOutlineIcon color="error" />
				<Typography variant="h6" noWrap sx={{ minWidth: 0 }}>
					{error?.title ?? 'Failed to save'}
				</Typography>
				<Box sx={{ flexGrow: 1 }} />
				{error && <CopyToClipboardButton
					value={[error.title, error.detail, error.traceId ? `Trace ID: ${error.traceId}` : undefined].filter(Boolean).join('\n\n')}
					title="Copy all error details"
					edge={false}
				/>}
				<DialogHeaderActions isMaximized={isMaximized} onToggleMaximized={() => setIsMaximized((value) => !value)} onClose={onClose} />
			</DialogTitle>
			<DialogContent dividers sx={{ flex: 1, overflowY: 'auto' }}>
				{error && error.validationIssues.length > 0 ? (
					<Stack spacing={2}>
						<Typography variant="body2">Fix the following issues before saving.</Typography>
						{error.validationIssues.map((issue, index) => (
							<Box key={`${issue.code}-${issue.path}-${index}`}>
								<Stack direction="row" alignItems="flex-start" spacing={1}>
									<Box sx={{ minWidth: 0, flexGrow: 1 }}>
										<Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
											{issue.message}
										</Typography>
										<Typography component="div" variant="caption" color="text.secondary" sx={{ mt: 0.5, wordBreak: 'break-all' }}>
											<Box component="span" sx={{ fontFamily: 'monospace' }}>
												{issue.code}
											</Box>
											{' at '}
											<Box component="span" sx={{ fontFamily: 'monospace' }}>
												{issue.path}
											</Box>
										</Typography>
									</Box>
									<CopyToClipboardButton value={`${issue.code} ${issue.path}: ${issue.message}`} title="Copy validation error" edge={false} />
								</Stack>
								{index < error.validationIssues.length - 1 && <Divider sx={{ mt: 2 }} />}
							</Box>
						))}
					</Stack>
				) : (
					<Alert severity="error" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
						<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
							{error?.detail}
						</Typography>
					</Alert>
				)}
				{error?.traceId && (
					<Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 2 }}>
						<Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
							Trace ID: {error.traceId}
						</Typography>
						<CopyToClipboardButton value={error.traceId} title="Copy trace ID" edge={false} />
					</Stack>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} variant="contained">
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
}
